import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import {
  GoogleMap,
  Libraries,
  MarkerF,
  OverlayView,
  useJsApiLoader,
} from '@react-google-maps/api';
import _ from 'lodash';

import { Property } from '../../Graphql/Generated/graphql';
import { formatUsd } from '../../Utils/GenericFunctions';
import ImageLoader from '../../legacy/Components/Generics/Image';
import { PropertyCard } from '../../new/pages/listings/components/PropertyCard';

interface IPropsMap {
  properties: any[];
  zoom?: number;
  width?: string;
  height?: string;
  position?: string;
  isMobile?: boolean;
}

const options = {
  fillColor: 'lightblue',
  fillOpacity: 1,
  strokeColor: 'red',
  strokeOpacity: 1,
  strokeWeight: 2,
  clickable: false,
  draggable: false,
  editable: false,
  geodesic: false,
  zIndex: 1,
};

const MapMarker = styled('div')`
  width: 32px;
  background-color: white;
  border: 1px solid #ccc;
  padding: 5px 10px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
`;

const HoverCardContainer = styled('div')`
  position: absolute;
  z-index: 1000;
  transform: translate(-40%, -100%);
  margin-top: -10px;
`;

const CardContainer = styled('div')`
  width: 200px;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const InfoSection = styled('div')`
  padding: 12px;
`;

const Title = styled('h3')`
  margin: 0 0 8px 0;
  font-size: 14px;
`;

const Description = styled('p')`
  font-size: 11px;
`;

const Price = styled('p')`
  margin: 0;
  font-size: 15px;
`;

export const PropertyImage = styled(ImageLoader)`
  width: 100%;
  object-fit: cover;
  position: relative;
`;

interface SmallPropertyCardProps {
  property: Property;
}

const SmallPropertyCard = ({ property }: SmallPropertyCardProps) => {
  const navigate = useNavigate();
  const urlSafeTitle = encodeURIComponent(property.title || '');

  return (
    <CardContainer>
      <PropertyImage
        // @ts-ignore
        onClick={() => navigate(`/property/${urlSafeTitle}`, { state: property })}
        width={200}
        height={150}
        filename={property?.images?.[0] || ''}
      />
      <InfoSection>
        <Title>{property.title}</Title>
        {property?.description && (
          <Description>{_.truncate(property.description)}</Description>
        )}
        <Price>{formatUsd(property?.totalRent)} Per Month</Price>
      </InfoSection>
    </CardContainer>
  );
};

const Map = (props: IPropsMap) => {
  const {
    zoom = 13,
    width = '100%',
    height = '100%',
    position = 'relative',
    isMobile,
    ...otherProps
  } = props;

  const [hoveredProperty, setHoveredProperty] = useState<Property | null>(null);
  const [clickedProperty, setClickedProperty] = useState<Property | null>(null);

  const mapStyles = {
    height,
    width,
    ...otherProps,
  };

  const styles = isMobile ? { display: 'none' } : { position, height: '100%', width: '100%' };

  // Memo needed to assure maps that this doesn't change every time the component code is hit; prevents reloads
  const libraries: Libraries = useMemo(() => ['places'], []);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API_KEY || '',
    libraries,
  });

  const defaultCenter = useMemo(() => {
    return _.isEmpty(props.properties)
      ? {
          lat: 24.886,
          lng: -70.268,
        }
      : {
          lat: Number(props.properties[0].location.latitude),
          lng: Number(props.properties[0].location.longitude),
        };
  }, [props.properties]);

  return (
    <>
      {isLoaded ? (
        // @ts-ignore
        <div style={styles}>
          <GoogleMap
            mapContainerStyle={{ ...mapStyles }}
            zoom={zoom}
            center={defaultCenter}
            onClick={() => setClickedProperty(null)}
          >
            {props.properties.map((val) => {
              return (
                <OverlayView
                  key={val.id}
                  position={{
                    lat: Number(val.location.latitude),
                    lng: Number(val.location.longitude),
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <MapMarker
                    onMouseEnter={() => setHoveredProperty(val)}
                    onMouseLeave={() => setHoveredProperty(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setClickedProperty((prevProperty) =>
                        prevProperty?.id === val.id ? null : val,
                      );
                    }}
                  >
                    {formatUsd(val.totalRent)}
                  </MapMarker>
                </OverlayView>
              );
            })}
            {(hoveredProperty || clickedProperty) && (
              <OverlayView
                position={{
                  lat: Number((hoveredProperty || clickedProperty)?.location?.latitude),
                  lng: Number((hoveredProperty || clickedProperty)?.location?.longitude),
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <HoverCardContainer>
                  <SmallPropertyCard property={hoveredProperty || clickedProperty!} />
                </HoverCardContainer>
              </OverlayView>
            )}
          </GoogleMap>
        </div>
      ) : (
        <Skeleton variant='rectangular' width='100%' height='100%' />
      )}
    </>
  );
};
export default Map;
